import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "providers/Layout";
import addressApi, {
  MUTATION_SYNC_UNIT_BY_ID,
  QUERY_SYNC_UNIT_BY_ID_STATUS
} from "api/landlord/address";
import { useAuthContext } from "providers/Auth";
import { Loader } from "shared/components/Loader";
import { Tabs } from "shared/components/Tabs";
import { Redirect } from "shared/components/Redirect";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { SingleSyncPropertyManagementSystems } from "utils/singleSyncPMS";
import {
  PropertyManagementSystemSyncStatus,
  PropertyManagementSystem
} from "shared/types";

import { Unit } from "types/landlord/address";
import { EditConfirmModal } from "views/properties-id-units-id/components/EditConfirmModal";
import { instance } from "api/apiBase";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import {
  EditModalContext,
  EditUnitSettings
} from "views/properties-id-units-id/types";
import { LayoutHeader } from "./components/LayoutHeader";
import { LayoutFooter } from "./components/LayoutFooter";
import { TabContent } from "./components/TabContent";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

const getTabs = (
  type: ReturnType<typeof useAuthContext>["type"],
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>
) => {
  const tabs: string[] = [`${dictionary.Units} details`];

  if (type.isBoomCRM) tabs.push("Leads");

  if (type.isBoomScreen) tabs.push("Applications");

  tabs.push("Discount codes");

  return tabs;
};

export const UnitDetail = () => {
  const { propertyId, unitId: unitIdParam } = useParams();
  const dictionary = useUnitsPropertiesDictionary();
  const { type, partner } = useAuthContext();

  const isSingleSyncSupported = SingleSyncPropertyManagementSystems.includes(
    partner?.property_management_system?.name as PropertyManagementSystem
  );

  // MobileView
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [tab, setActiveTab] = useState<string>(`${dictionary.Units} details`);

  const [showEditModal, setShowEditModal] = useState<{
    isOpen: boolean;
    context?: EditModalContext;
  }>({
    isOpen: false,
    context: undefined
  });

  const [unitId, setunitId] = useState<number | null>(null);

  const {
    data: unit,
    isLoading,
    refetch
  } = useQuery<Unit>({
    queryKey: ["unit", propertyId, unitIdParam],
    queryFn: () =>
      addressApi.getUnitByPropertyAndUnitId(
        String(propertyId),
        String(unitIdParam)
      )
  });

  useEffect(() => {
    if (unit?.id && unit.id !== unitId) setunitId(unit.id);
  }, [unit, unitId]);

  const { mutateAsync } = useMutation({
    mutationKey: ["unit", unitId],
    mutationFn: async ({
      unitId,
      settings
    }: {
      unitId: number;
      settings: EditUnitSettings;
    }) => {
      await instance.patch(`/portal/v1/units/${unitId}`, { ...settings });
    },
    onSuccess: () => {
      setShowEditModal({ isOpen: false });
      refetch();
    }
  });

  const [isSyncing, setIsSyncing] = useState(type.isPMS);

  const { data: syncData, isError: syncError } =
    useQuery<PropertyManagementSystemSyncStatus>({
      queryKey: [QUERY_SYNC_UNIT_BY_ID_STATUS, unitIdParam],
      queryFn: () =>
        addressApi.getPropertyManagementSystemSyncByIdStatus(
          "units",
          unitIdParam || ""
        ),
      refetchInterval: 4000,
      enabled: isSyncing && !!unitIdParam
    });

  const { mutate: handleRequestSync } = useMutation({
    mutationFn: async () => {
      return addressApi
        .requestPropertyManagementSystemSyncById("units", unitIdParam || "")
        .then(() => {
          toast.success("Sync unit requested successfully");
          setIsSyncing(true);
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
          setIsSyncing(false);
        });
    },
    mutationKey: [MUTATION_SYNC_UNIT_BY_ID, unitIdParam]
  });

  useEffect(() => {
    setIsSyncing(type.isPMS);
  }, [type.isPMS]);

  useEffect(() => {
    const isSyncRunning = syncData?.status === "running";
    setIsSyncing(isSyncRunning);

    if (!isSyncRunning) refetch();
  }, [syncData, syncError]);

  if (type.isSDK && type.isBoomScreen) return <Redirect href="/" />;

  if (isLoading)
    return (
      <Layout>
        <div className="flex items-center justify-center w-full h-full">
          <Loader />
        </div>
      </Layout>
    );

  return (
    <Layout
      footerClassName="p-0"
      footer={<LayoutFooter unit={unit} propertyId={propertyId} />}
      headerClassName="py-0 md:py-3 border-b border-alpha-100"
      header={
        <LayoutHeader
          unit={unit}
          isSingleSyncSupported={isSingleSyncSupported}
          isSyncing={isSyncing}
          handleRequestSync={handleRequestSync}
          lastSuccessSyncedAt={syncData?.last_successful_sync}
        />
      }
    >
      {type.isBoomScreen ? (
        <div className="space-y-4">
          <Tabs
            tabs={getTabs(type, dictionary)}
            selected={tab}
            setActiveTab={(t) => setActiveTab(t as string)}
            variant="underline"
          />
          <TabContent tab={tab} unit={unit} />
        </div>
      ) : null}
      <TemplateReviewModal
        opened={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        id={unit?.verification_template?.id}
      />
      <EditConfirmModal
        onChange={() => {
          if (!showEditModal.context) return;

          const overrideMap = {
            [EditModalContext.RentAmount]: {
              pms_monthly_rent_amount_override: false
            },
            [EditModalContext.HomeRentAmount]: {
              pms_home_rent_amount_override: false
            },
            [EditModalContext.SiteRentAmount]: {
              pms_site_rent_amount_override: false
            },
            [EditModalContext.CurrentStatus]: {
              pms_occupied_override: false,
              pms_available_at_override: false
            }
          };
          const override = overrideMap[showEditModal.context];

          mutateAsync({
            unitId: Number(unitId),
            settings: override
          });
        }}
        context={showEditModal.context}
        opened={showEditModal.isOpen}
        onClose={() => setShowEditModal({ isOpen: false })}
      />
    </Layout>
  );
};
